import { useState } from "react";
import { Search } from "lucide-react";

export type SortDir = "asc" | "desc";

/** Szukajka nad tabelą — filtruje listę już pobraną do przeglądarki. */
export function TableSearchInput({
  value, onChange, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative mb-2">
      <Search
        size={14}
        className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="text-xs border border-gray-200 dark:border-gray-600 rounded pl-7 pr-2 py-1.5 w-64 max-w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
      />
    </div>
  );
}

/** Sort state shared by the clickable table headers and the mobile sort bar. */
export function useTableSort<F extends string>() {
  const [sortField, setSortField] = useState<F | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function handleSort(field: F) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function setField(field: F | null) {
    setSortField(field);
    setSortDir("asc");
  }

  function toggleDir() {
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  }

  return { sortField, sortDir, handleSort, setField, toggleDir };
}

export function SortableTh<F extends string>({
  field, label, sortField, sortDir, onSort, align = "left",
}: {
  field: F;
  label: string;
  sortField: F | null;
  sortDir: SortDir;
  onSort: (f: F) => void;
  align?: "left" | "right";
}) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      className={`${align === "right" ? "text-right" : "text-left"} px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 whitespace-nowrap`}
    >
      {label}{" "}
      <span className={active ? "text-[var(--color-primary)]" : "opacity-30"}>
        {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

/** Mobilny odpowiednik klikanych nagłówków — select z polem + przycisk kierunku. */
export function MobileSortBar<F extends string>({
  id, labels, sortField, sortDir, onFieldChange, onDirToggle,
}: {
  id: string;
  labels: Record<F, string>;
  sortField: F | null;
  sortDir: SortDir;
  onFieldChange: (f: F | null) => void;
  onDirToggle: () => void;
}) {
  return (
    <div className="sm:hidden flex items-center gap-2 mb-2">
      <label className="text-xs text-gray-500 dark:text-gray-400" htmlFor={id}>Sortuj:</label>
      <select
        id={id}
        className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        value={sortField ?? ""}
        onChange={(e) => onFieldChange((e.target.value || null) as F | null)}
      >
        <option value="">Domyślnie</option>
        {(Object.keys(labels) as F[]).map((f) => (
          <option key={f} value={f}>{labels[f]}</option>
        ))}
      </select>
      {sortField && (
        <button
          type="button"
          className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          onClick={onDirToggle}
          title={sortDir === "asc" ? "Rosnąco" : "Malejąco"}
        >
          {sortDir === "asc" ? "↑" : "↓"}
        </button>
      )}
    </div>
  );
}
